import { useMemo, useState } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useStore } from '../../hooks/useStore';
import { BottomNav } from './BottomNav';
import { SlideMenu } from './SlideMenu';
import { AvatarMenu } from './AvatarMenu';
import { Sidebar } from './Sidebar';
import { ConfirmModal } from '../base/ConfirmModal';
import {
  getNotificationsByFarmer,
  getUnreadNotificationsByFarmer,
  getLoansByHtx,
  getFarmersByHtx,
  getDebtsByFarmer,
  getActivePaymentIntentByLoan,
} from '../../store';
import type { KycStatus, Notification } from '../../types';

const CREATE_PROFILE_PATH = '/nong-ho/tao-ho-so';

/** Reminder shown above every page while eKYC is unresolved — replacing the old
 * redirect that locked the whole app on /ekyc. Not shown before a first
 * submission: the overview pages already prompt for that. */
const EKYC_NOTICE: Record<string, { text: string; tone: string }> = {
  ekyc_pending: {
    text: 'eKYC đang chờ duyệt — bạn vẫn dùng được mọi chức năng, chỉ xin hạn mức và vay vốn cần chờ duyệt xong.',
    tone: 'bg-amber-50 text-amber-800',
  },
  ekyc_need_more: {
    text: 'eKYC cần bổ sung thông tin để xin hạn mức và vay vốn. Bấm để bổ sung.',
    tone: 'bg-amber-50 text-amber-800',
  },
  ekyc_failed: {
    text: 'eKYC chưa được duyệt — cần làm lại để xin hạn mức và vay vốn. Bấm để làm lại.',
    tone: 'bg-red-50 text-red-700',
  },
};

export function PrivateLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = useStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const unreadNotificationCount = user?.role === 'Nông hộ' && user.farmerId
    ? getUnreadNotificationsByFarmer(user.farmerId).length
    : 0;

  const htxActivityItems = useMemo(
    () => buildHtxActivityItems(state, user?.role === 'Hợp tác xã' ? user.htxId : undefined),
    [state, user?.role, user?.htxId],
  );
  const htxActivityCount = htxActivityItems.filter((item) => item.urgent).length || htxActivityItems.length;
  const farmerNotifications = useMemo(
    () => user?.role === 'Nông hộ' && user.farmerId ? getNotificationsByFarmer(user.farmerId).slice(0, 6) : [],
    [user?.farmerId, user?.role],
  );

  const toggleNoticePopup = () => {
    setAvatarMenuOpen(false);
    setNoticeOpen((open) => !open);
  };

  if (!user) {
    return <Navigate to="/dang-nhap" replace />;
  }

  // A self-registered Farmer with no Farmer profile yet gets sent to create one
  // first — mirrors the Flutter app's reactive `farmerId == null` gate — instead of
  // falling through to pages that need a real farmerId to show anything.
  if (user.role === 'Nông hộ' && !user.farmerId && location.pathname !== CREATE_PROFILE_PATH) {
    return <Navigate to={CREATE_PROFILE_PATH} replace />;
  }

  // No eKYC gate here on purpose. eKYC only matters for borrowing (hạn mức,
  // vay, giải ngân) and is enforced at those actions. A pending/need-more/failed
  // status used to bounce every route to /ekyc, so an HTX that submitted its
  // own eKYC — reviewed by ADMIN/OPS, which can take days — could not create a
  // farmer, a season or a contract until someone approved it.

  // Cỡ chữ (Cài đặt → Hiển thị) — 0/1/2 picked in AccountSettingsSheet, persisted via
  // PATCH /auth/me. `zoom` scales the whole shell uniformly, same approach the brand
  // mockup used for its accessibility font-size picker.
  const FONT_ZOOM: Record<0 | 1 | 2, number> = { 0: 0.92, 1: 1, 2: 1.15 };

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex overflow-x-hidden" style={{ zoom: FONT_ZOOM[user.fontSize ?? 1] }}>
      {/* Desktop sidebar */}
      <Sidebar
        user={user}
        onLogout={logout}
        collapsed={sidebarCollapsed}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 max-w-[430px] md:max-w-[720px] lg:max-w-none mx-auto w-full">
        {/* Header — only on mobile/tablet, hidden on desktop since sidebar has branding */}
        <header className="h-14 bg-agrida-600 text-white flex items-center justify-between px-3 sm:px-4 shrink-0 sticky top-0 z-40 lg:hidden shadow-sm">
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-white/15 transition-colors active:scale-95"
              aria-label="Mở menu"
            >
              <i className="ri-menu-line text-xl" />
            </button>
            <span className="w-7 h-7 flex items-center justify-center">
              <i className="ri-plant-line text-xl" />
            </span>
            <span className="font-bold text-base">Agrida</span>
          </div>

          {/* Desktop sidebar toggle — visible when sidebar exists */}
          <div className="hidden lg:flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-white/15 transition-colors"
              aria-label={sidebarCollapsed ? 'Mở rộng sidebar' : 'Thu gọn sidebar'}
            >
              <i className={`text-lg ${sidebarCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'}`} />
            </button>
          </div>

          <div className="flex items-center gap-1">
            {user.role === 'Nông hộ' && (
              <button
                type="button"
                onClick={toggleNoticePopup}
                className="relative flex h-9 w-9 items-center justify-center rounded-xl hover:bg-white/15 transition-colors active:scale-95"
                aria-label="Mở thông báo"
              >
                <i className="ri-notification-3-line text-lg" />
                {unreadNotificationCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-rose-500 px-1 text-[9px] font-bold leading-4 text-white">
                    {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                  </span>
                )}
              </button>
            )}
            {user.role === 'Hợp tác xã' && (
              <button
                type="button"
                onClick={toggleNoticePopup}
                className="relative flex h-9 w-9 items-center justify-center rounded-xl hover:bg-white/15 transition-colors active:scale-95"
                aria-label="Mở hoạt động gần đây"
              >
                <i className="ri-notification-3-line text-lg" />
                {htxActivityCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-rose-500 px-1 text-[9px] font-bold leading-4 text-white">
                    {htxActivityCount > 9 ? '9+' : htxActivityCount}
                  </span>
                )}
              </button>
            )}

            {/* Avatar button — AvatarMenu handles its own overlay */}
            <button
              type="button"
              onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
              className="flex items-center gap-2 py-1 px-1.5 rounded-xl hover:bg-white/15 transition-colors active:scale-95"
              aria-label="Mở menu tài khoản"
              aria-expanded={avatarMenuOpen}
            >
              <span className="text-xs font-medium truncate max-w-[100px] opacity-90 hidden sm:inline">
                {user.displayName}
              </span>
              <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                {user.displayName.charAt(0)}
              </span>
            </button>
          </div>
        </header>

        {/* Desktop header — compact, only shown when sidebar exists */}
        <header className="hidden lg:flex h-14 bg-white border-b border-gray-100 items-center justify-end gap-2 px-6 shrink-0 sticky top-0 z-30">
          {user.role === 'Nông hộ' && (
            <button
              type="button"
              onClick={toggleNoticePopup}
              className="relative flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 transition-colors active:scale-95"
              aria-label="Mở thông báo"
            >
              <i className="ri-notification-3-line text-lg" />
              {unreadNotificationCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-rose-500 px-1 text-[9px] font-bold leading-4 text-white">
                  {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                </span>
              )}
            </button>
          )}
          {user.role === 'Hợp tác xã' && (
            <button
              type="button"
              onClick={toggleNoticePopup}
              className="relative flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 transition-colors active:scale-95"
              aria-label="Mở hoạt động gần đây"
            >
              <i className="ri-notification-3-line text-lg" />
              {htxActivityCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-rose-500 px-1 text-[9px] font-bold leading-4 text-white">
                  {htxActivityCount > 9 ? '9+' : htxActivityCount}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
            className="flex items-center gap-2 py-1 px-1.5 rounded-xl hover:bg-gray-100 transition-colors active:scale-95"
            aria-label="Mở menu tài khoản"
            aria-expanded={avatarMenuOpen}
          >
            <span className="text-sm font-medium text-gray-700 truncate max-w-[160px]">
              {user.displayName}
            </span>
            <span className="w-8 h-8 rounded-full bg-agrida-100 flex items-center justify-center text-agrida-700 text-sm font-bold">
              {user.displayName.charAt(0)}
            </span>
          </button>
        </header>

        <main className="flex-1 pb-24 lg:pb-8 overflow-y-auto overflow-x-hidden">
          {EKYC_NOTICE[user.kycStatus ?? ''] && location.pathname !== '/ekyc' && (
            <button
              type="button"
              onClick={() => navigate('/ekyc')}
              className={`flex w-full items-center gap-2 px-4 py-2 text-left text-xs ${EKYC_NOTICE[user.kycStatus ?? ''].tone}`}
            >
              <i className="ri-shield-user-line text-sm shrink-0" />
              <span className="flex-1">{EKYC_NOTICE[user.kycStatus ?? ''].text}</span>
              <i className="ri-arrow-right-s-line text-sm shrink-0" />
            </button>
          )}
          <Outlet />
        </main>

        {/* Bottom nav — mobile/tablet only */}
        <div className="lg:hidden">
          <BottomNav user={user} />
        </div>
      </div>

      {/* Avatar menu — self-contained with its own overlay */}
      <AvatarMenu
        open={avatarMenuOpen}
        onClose={() => setAvatarMenuOpen(false)}
        user={{
          displayName: user.displayName,
          role: user.role,
          kycStatus: (user.kycStatus || 'registered') as KycStatus,
          creditScore: user.creditScore,
          code: user.code,
        }}
        onLogout={logout}
      />

      {noticeOpen && (
        <CompactNoticePopup
          role={user.role}
          notifications={farmerNotifications}
          htxItems={htxActivityItems}
          onClose={() => setNoticeOpen(false)}
          onOpenAll={() => {
            setNoticeOpen(false);
            navigate(user.role === 'Nông hộ' ? '/nong-ho/thong-bao' : '/htx/vay-han-muc');
          }}
        />
      )}

      {/* Slide menu — self-contained with its own overlay */}
      <SlideMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={{
          displayName: user.displayName,
          role: user.role,
          kycStatus: (user.kycStatus || 'registered') as KycStatus,
          creditScore: user.creditScore,
          code: user.code,
        }}
      />

      <ConfirmModal />
    </div>
  );
}

interface HtxActivityItem {
  id: string;
  title: string;
  content: string;
  icon: string;
  tone: 'emerald' | 'amber' | 'rose' | 'sky' | 'slate';
  urgent?: boolean;
}

/** Duyệt vay/Cấp hạn mức/Giải ngân quick-glance items were removed — those are
 * Agrida-internal actions with no accessible endpoint for HTX_STAFF (see task 27
 * notes). This feed now surfaces real, HTX-accessible signals only: loans needing
 * review/more-info, and repayments awaiting HTX confirmation. */
function buildHtxActivityItems(state: ReturnType<typeof useStore>, htxId?: string): HtxActivityItem[] {
  const pendingLoans = getLoansByHtx(htxId).filter((loan) => ['Chờ kiểm tra', 'Cần bổ sung'].includes(loan.status));
  const htxFarmers = getFarmersByHtx(htxId);
  const paymentIntentsAwaitingConfirm = htxFarmers
    .flatMap((farmer) => getDebtsByFarmer(farmer.id).map((debt) => getActivePaymentIntentByLoan(farmer.id, debt.advanceRequestId || debt.id)))
    .filter((intent) => !!intent && ['farmer_confirmed_transfer', 'waiting_htx_confirmation', 'reconcile_failed'].includes(intent.status));
  const farmerNameMap = new Map(state.farmers.map((farmer) => [farmer.id, farmer.name]));
  const recentItems: HtxActivityItem[] = [...state.notifications]
    .sort((a, b) => new Date(b.ngayTao).getTime() - new Date(a.ngayTao).getTime())
    .slice(0, 5)
    .map((notification) => ({
      id: `recent-${notification.id}`,
      title: farmerNameMap.get(notification.farmerId) || notification.title,
      content: notification.content,
      icon: notification.type === 'loan_approved' || notification.type === 'disburse_success' ? 'ri-checkbox-circle-line'
        : notification.type === 'debt_overdue' || notification.type === 'disburse_failed' || notification.type === 'loan_rejected' ? 'ri-alert-line'
        : 'ri-notification-3-line',
      tone: notification.type === 'loan_approved' || notification.type === 'disburse_success' ? 'emerald'
        : notification.type === 'debt_overdue' || notification.type === 'disburse_failed' || notification.type === 'loan_rejected' ? 'amber'
        : 'slate',
    }));

  return [
    pendingLoans.length > 0 && {
      id: 'loan',
      title: `${pendingLoans.length} yêu cầu vay cần xử lý`,
      content: 'Có yêu cầu vay của nông hộ đang chờ HTX kiểm tra hoặc yêu cầu bổ sung.',
      icon: 'ri-hand-coin-line',
      tone: 'amber' as const,
      urgent: true,
    },
    paymentIntentsAwaitingConfirm.length > 0 && {
      id: 'debt',
      title: `${paymentIntentsAwaitingConfirm.length} giao dịch trả nợ chờ xác nhận`,
      content: 'Nông hộ đã xác nhận chuyển khoản, HTX cần đối soát công nợ.',
      icon: 'ri-bank-card-line',
      tone: 'rose' as const,
      urgent: true,
    },
    ...recentItems,
  ].filter(Boolean) as HtxActivityItem[];
}

function CompactNoticePopup({
  role,
  notifications,
  htxItems,
  onClose,
  onOpenAll,
}: {
  role: string;
  notifications: Notification[];
  htxItems: HtxActivityItem[];
  onClose: () => void;
  onOpenAll: () => void;
}) {
  const isFarmer = role === 'Nông hộ';
  const toneClass = (tone: HtxActivityItem['tone']) => {
    if (tone === 'emerald') return 'bg-agrida-50 text-agrida-700';
    if (tone === 'amber') return 'bg-amber-50 text-amber-700';
    if (tone === 'rose') return 'bg-rose-50 text-rose-700';
    if (tone === 'sky') return 'bg-sky-50 text-sky-700';
    return 'bg-slate-50 text-slate-700';
  };

  return (
    <>
      <div className="fixed inset-0 z-[88]" onClick={onClose} />
      <div className="fixed right-[max(12px,calc((100vw-430px)/2+12px))] top-14 z-[89] w-[min(380px,calc(100vw-24px))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900">{isFarmer ? 'Thông báo' : 'Hoạt động HTX'}</h3>
            <p className="text-[10px] text-slate-400">{isFarmer ? 'Các cập nhật mới nhất' : 'Các việc cần chú ý gần đây'}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="max-h-[58dvh] space-y-2 overflow-y-auto p-3">
          {isFarmer ? (
            notifications.length === 0 ? (
              <p className="py-6 text-center text-xs text-slate-400">Chưa có thông báo mới</p>
            ) : notifications.map((item) => (
              <article key={item.id} className={`rounded-xl border p-3 ${item.daDoc ? 'border-slate-100 bg-slate-50' : 'border-agrida-100 bg-agrida-50/50'}`}>
                <div className="flex gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-agrida-600">
                    <i className="ri-notification-3-line" />
                  </span>
                  <div className="min-w-0">
                    <p className="line-clamp-1 text-xs font-bold text-slate-900">{item.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-600">{item.content}</p>
                    <p className="mt-1 text-[10px] text-slate-400">{new Date(item.ngayTao).toLocaleString('vi-VN')}</p>
                  </div>
                </div>
              </article>
            ))
          ) : (
            htxItems.length === 0 ? (
              <p className="py-6 text-center text-xs text-slate-400">Chưa có hoạt động cần xử lý</p>
            ) : htxItems.map((item) => (
              <article key={item.id} className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
                <div className="flex gap-2">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${toneClass(item.tone)}`}>
                    <i className={item.icon} />
                  </span>
                  <div className="min-w-0">
                    <p className="line-clamp-1 text-xs font-bold text-slate-900">{item.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-600">{item.content}</p>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
        <div className="border-t border-slate-100 p-3">
          <button type="button" onClick={onOpenAll} className="min-h-10 w-full rounded-xl bg-agrida-600 px-4 text-xs font-bold text-white">
            {isFarmer ? 'Xem tất cả thông báo' : 'Mở module xử lý'}
          </button>
        </div>
      </div>
    </>
  );
}
